import { useState } from 'react';

// Import hooks
import useStoredPlanners from '../../hooks/schedule/useStoredPlanners';

// Import types
import type { DateRange } from '../../types/date';

// Import components
import EventFormPanel from './EventFormPanel';
import SavedPlannersPanel from './SavedPlannersPanel';

type ModalTab = 'create' | 'saved';

type Props = {
  isOpen: boolean;
  isOwner: boolean;
  title?: string;
  onTitleChange: (v: string) => void;
  timezone: string;
  onTimezoneChange: (tz: string) => void;
  pickerRange: DateRange;
  onPickerRangeChange: (range: DateRange) => void;
  pickerStart: string; // HH:MM
  onPickerStartChange: (v: string) => void;
  pickerEnd: string; // HH:MM
  onPickerEndChange: (v: string) => void;
  onClose: () => void;
  onSave: () => void;
  showSavedPlanners?: boolean;
  initialTab?: ModalTab;
  onOpenSavedPlanner?: (id: string) => void;
};

const EventModal = (props: Props) => {
  const {
    isOpen,
    isOwner,
    title,
    onTitleChange,
    timezone,
    onTimezoneChange,
    pickerRange,
    onPickerRangeChange,
    pickerStart,
    onPickerStartChange,
    pickerEnd,
    onPickerEndChange,
    onClose,
    onSave,
    showSavedPlanners = false,
    initialTab = 'create',
    onOpenSavedPlanner,
  } = props;

  const [activeTab, setActiveTab] = useState<ModalTab>(initialTab);

  const {
    planners,
    isRefreshing,
    refreshPlanners,
    confirmRemove,
    copyOrShareLink,
    openPlanner,
  } = useStoredPlanners({
    isOpen: isOpen && showSavedPlanners,
    isSavedTabActive: isOpen && showSavedPlanners && activeTab === 'saved',
  });

  if (!isOpen) return null;

  const handleOpenSavedPlanner = (id: string) => {
    if (onOpenSavedPlanner) {
      openPlanner(id, onOpenSavedPlanner);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="flex flex-col w-full max-w-[420px] gap-3 max-h-[calc(100dvh-1rem)] sm:max-h-[90vh]">
        {showSavedPlanners && (
          <div className="shrink-0 bg-white rounded-2xl shadow-xl p-1 flex">
            <button
              type="button"
              onClick={() => setActiveTab('create')}
              className={`flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors ${
                activeTab === 'create'
                  ? 'bg-green-700 text-white'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}>
              Create
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('saved')}
              className={`flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors flex items-center justify-center gap-2 ${
                activeTab === 'saved'
                  ? 'bg-green-700 text-white'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}>
              Saved
              {planners.length > 0 && (
                <span
                  className={`inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full text-xs font-bold ${
                    activeTab === 'saved'
                      ? 'bg-white/20 text-white'
                      : 'bg-gray-200 text-gray-700'
                  }`}>
                  {planners.length}
                </span>
              )}
            </button>
          </div>
        )}

        <div className="relative bg-white w-full rounded-3xl shadow-xl flex flex-col overflow-hidden max-h-[calc(100dvh-1rem)] sm:max-h-[90vh]">
          {showSavedPlanners ? (
            <>
              <div
                className={`flex flex-col flex-1 min-h-0 ${
                  activeTab === 'create' ? '' : 'invisible pointer-events-none'
                }`}
                aria-hidden={activeTab !== 'create'}>
                <EventFormPanel
                  className="flex-1 min-h-0 h-full"
                  isOwner={isOwner}
                  title={title}
                  onTitleChange={onTitleChange}
                  timezone={timezone}
                  onTimezoneChange={onTimezoneChange}
                  pickerRange={pickerRange}
                  onPickerRangeChange={onPickerRangeChange}
                  pickerStart={pickerStart}
                  onPickerStartChange={onPickerStartChange}
                  pickerEnd={pickerEnd}
                  onPickerEndChange={onPickerEndChange}
                  onClose={onClose}
                  onSave={onSave}
                />
              </div>
              <div
                className={`absolute inset-0 flex flex-col bg-white ${
                  activeTab === 'saved' ? '' : 'invisible pointer-events-none'
                }`}
                aria-hidden={activeTab !== 'saved'}>
                <SavedPlannersPanel
                  className="h-full min-h-0"
                  planners={planners}
                  isRefreshing={isRefreshing}
                  onRefresh={() => refreshPlanners({ showToast: true })}
                  onOpen={handleOpenSavedPlanner}
                  onCopyOrShare={copyOrShareLink}
                  onRemove={confirmRemove}
                  onClose={onClose}
                />
              </div>
            </>
          ) : (
            <EventFormPanel
              className="flex-1 min-h-0 h-full"
              isOwner={isOwner}
              title={title}
              onTitleChange={onTitleChange}
              timezone={timezone}
              onTimezoneChange={onTimezoneChange}
              pickerRange={pickerRange}
              onPickerRangeChange={onPickerRangeChange}
              pickerStart={pickerStart}
              onPickerStartChange={onPickerStartChange}
              pickerEnd={pickerEnd}
              onPickerEndChange={onPickerEndChange}
              onClose={onClose}
              onSave={onSave}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default EventModal;
